import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useToast } from "@/components/ui/status/Toast/useToast";
import { BizProfileQueries } from "@/data/bizProfile/bizProfile.queries";
import { ProfileUploadsContext, type UploadBatch } from "@/data/uploads/uploads.context";

const useUploadedFilesBulkCreateProfiles = () => {
  const processingRef = useRef<Set<string>>(new Set());
  const { mutateAsync: bulkCreateProfiles } = BizProfileQueries.useBulkCreate();
  const { successToast, errorToast } = useToast();
  const queryClient = useQueryClient();
  const invalidateBizProfileList = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [...BizProfileQueries.keys.all],
    });
  }, [queryClient]);

  const { t } = useTranslation();

  const { removeBatchFromQueue, setBatchProcessing } = ProfileUploadsContext();

  const splitArrayIntoChunks = (array: string[], chunkSize: number) => {
    return array.reduce((result, item, index) => {
      const chunkIndex = Math.floor(index / chunkSize);
      const newResult = [...result];

      if (!newResult[chunkIndex]) {
        newResult[chunkIndex] = [];
      }
      newResult[chunkIndex] = [...newResult[chunkIndex], item];

      return newResult;
    }, [] as string[][]);
  };

  const processCompletedBatch = useCallback(
    async (batch: UploadBatch) => {
      // Prevent duplicate processing
      if (processingRef.current.has(batch.id)) {
        return;
      }

      try {
        processingRef.current.add(batch.id);

        // Check if all files in the batch are uploaded
        const allFilesUploaded = batch.files.every((file) => file.status === "uploaded");

        if (allFilesUploaded && batch.uploadedMediaIds.length === batch.files.length) {
          const splitUploadedMediaIds = splitArrayIntoChunks(batch.uploadedMediaIds, 10);
          for (const mediaIds of splitUploadedMediaIds) {
            await bulkCreateProfiles({
              data: {
                fileIds: mediaIds,
                tags: batch.tags.map((tag) => tag.name),
                location: batch.location,
                jobTitle: batch.jobTitle,
              },
            });
          }

          // Invalidate queries and show success message
          await invalidateBizProfileList();
          successToast({
            text: t("uploads.files-upload-modal.success-title"),
          });

          // Remove the batch from queue
          removeBatchFromQueue(batch.id);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : t("uploads.files-upload-modal.error-title");
        errorToast({
          text: errorMessage,
        });
        // Mark batch as not processing on error
        setBatchProcessing(batch.id, false);
      } finally {
        processingRef.current.delete(batch.id);
      }
    },
    [
      bulkCreateProfiles,
      invalidateBizProfileList,
      successToast,
      t,
      removeBatchFromQueue,
      setBatchProcessing,
      errorToast,
    ],
  );

  return {
    processCompletedBatch,
  };
};

export default useUploadedFilesBulkCreateProfiles;
